import { useState, useCallback } from 'react';
import { getTaskDates } from '../utils';
import { MIN_DURATION_MINUTES } from '../../../../utils/taskDuration';

const MS_PER_MINUTE = 60 * 1000;

/**
 * useGanttMultiDrag Hook
 *
 * Handles multi-selection drag state for moving/resizing multiple tasks at once.
 *
 * @param {Function} onTaskDateChange - (taskId, newStart, deltaOrNewDurationMinutes, dragType)
 */
export function useGanttMultiDrag({
  selectedTaskIds,
  filteredTasks,
  onTaskDateChange,
}) {
  const [multiDragDelta, setMultiDragDelta] = useState(0);
  const [isDraggingMultiple, setIsDraggingMultiple] = useState(false);
  const [multiDragType, setMultiDragType] = useState(null);

  const handleMultiDrag = useCallback((deltaPercent, isCommit, deltaMinutes = 0, dragType = 'move') => {
    if (isCommit) {
      if (deltaMinutes !== 0 && onTaskDateChange) {
        selectedTaskIds.forEach(taskId => {
          const task = filteredTasks.find(t => t.id === taskId);
          if (!task) return;

          const dates = getTaskDates(task);
          if (!dates) return;

          if (dragType === 'move') {
            const newStart = new Date(dates.startDate.getTime() + deltaMinutes * MS_PER_MINUTE);
            onTaskDateChange(taskId, newStart, deltaMinutes, 'move');
          } else if (dragType === 'resize-start') {
            const newStart = new Date(dates.startDate.getTime() + deltaMinutes * MS_PER_MINUTE);
            onTaskDateChange(taskId, newStart, deltaMinutes, 'resize-start');
          } else if (dragType === 'resize-end') {
            const currentMs = dates.endDate - dates.startDate;
            const currentMinutes = Math.max(
              MIN_DURATION_MINUTES,
              Math.round(currentMs / MS_PER_MINUTE),
            );
            const newDuration = Math.max(MIN_DURATION_MINUTES, currentMinutes + deltaMinutes);
            onTaskDateChange(taskId, null, newDuration, 'resize-end');
          }
        });
      }
      setMultiDragDelta(0);
      setIsDraggingMultiple(false);
      setMultiDragType(null);
    } else {
      setMultiDragDelta(deltaPercent);
      setIsDraggingMultiple(true);
      setMultiDragType(dragType);
    }
  }, [selectedTaskIds, filteredTasks, onTaskDateChange]);

  return {
    multiDragDelta,
    isDraggingMultiple,
    multiDragType,
    handleMultiDrag,
  };
}
